<template>
  <div
    class="font-inter h-[1040px] mt-3 rounded-t-[20px] bg-primaryLight pb-10"
  >
    <NavbarComponent />

    <div
      class="h-16 my-2 px-8 bg-primaryLight space-y-6 flex items-center justify-between"
    >
      <div class="space-y-3">
        <h1 v-if="loading">Loading...</h1>
        <h1 v-else class="text-[20px] text-bodyText">
          Welcome back {{ user.first_name }},
        </h1>
        <p class="text-[14px] text-grayDark">
          Get a quick snapshot of your key metrics, recent activity, and
          essential updates
        </p>
      </div>

      <div class="relative group">
        <button
          @click="emitOpenCreateProjectModal"
          class="flex bg-primaryDarkBg hover:bg-primaryDark text-primaryLight text-[14px] font-medium px-6 py-3 space-x-4 items-center rounded-[8px] h-[42px]"
          :disabled="goalLimit && goalLimit.is_limit_reached"
        >
          <img src="@/assets/dashboard/add.svg" alt="Add" class="h-4" />
          <span>New Goal</span>
        </button>

        <div
          v-if="goalLimit && goalLimit.is_limit_reached"
          class="absolute right-0 top-full mt-2 w-max bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-50"
        >
          You have reached the maximum number of goals you can create.
        </div>
      </div>
    </div>

    <div class="px-8 space-y-6">
      <!-- Metrics pill -->
      <div
        class="rounded-full px-6 py-3 flex items-center justify-between mx-auto"
        style="
          border: 1px solid #eeeeee;
          border-radius: 100px;
          background: linear-gradient(to bottom, #ffffff, #f3f4f6);
          max-width: 960px;
        "
      >
        <div class="flex items-center space-x-3">
          <span class="text-[20px] font-semibold text-primaryDark">{{
            loadingOverview ? "--" : completedGoalsCount
          }}</span>
          <span class="text-[12px] text-grayDark"
            >Completed Learning Goals</span
          >
        </div>
        <div class="h-6 w-px bg-grayLight"></div>
        <div class="flex items-center space-x-3">
          <span class="text-[20px] font-semibold text-primaryDark">{{
            loadingOverview ? "--" : activeTasksCount
          }}</span>
          <span class="text-[12px] text-grayDark">Active Tasks</span>
        </div>
        <div class="h-6 w-px bg-grayLight"></div>
        <div class="flex items-center space-x-3">
          <span class="text-[20px] font-semibold text-primaryDark">{{
            loadingOverview ? "--" : teamMembersCount
          }}</span>
          <span class="text-[12px] text-grayDark">Team Members</span>
        </div>
        <div class="h-6 w-px bg-grayLight"></div>
        <div class="flex items-center space-x-3">
          <span class="text-[20px] font-semibold text-primaryDark"
            >{{ loadingOverview ? "--" : completionRate }}%</span
          >
          <span class="text-[12px] text-grayDark">Completion Rate</span>
        </div>
      </div>

      <!-- Main cards -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div class="space-y-6">
          <!-- Streak -->
          <div
            class="bg-primaryLight rounded-[12px] border border-grayLight p-6"
          >
            <div class="flex items-start justify-between">
              <div class="space-y-1">
                <p class="text-[14px] font-semibold text-bodyText">
                  {{ streakDays }} days streak
                </p>
                <p class="text-[13px] text-grayDark">
                  You’re on your way to 100 days, keep going
                  {{ user.first_name || "!" }}
                </p>
              </div>
              <div class="h-6">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 48 48"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g style="mix-blend-mode: difference">
                    <path
                      d="M24 48C33.942 48 42 42 42 31.5C42 27 40.5 19.5 34.5 13.5C35.25 18 30.75 19.5 30.75 19.5C33 12 27 1.5 18 0C19.071 6 19.5 12 12 18C8.25 21 6 26.187 6 31.5C6 42 14.058 48 24 48ZM24 45C19.029 45 15 42 15 36.75C15 34.5 15.75 30.75 18.75 27.75C18.375 30 21 31.5 21 31.5C19.875 27.75 22.5 21.75 27 21C26.463 24 26.25 27 30 30C31.875 31.5 33 34.092 33 36.75C33 42 28.971 45 24 45Z"
                      fill="#222222"
                    />
                  </g>
                </svg>
              </div>
            </div>

            <div class="mt-4 rounded-[8px] border border-grayLight px-4 py-3">
              <div class="grid grid-cols-7 gap-2 text-center">
                <span
                  v-for="day in weekActivity"
                  :key="day.date"
                  class="text-[11px] text-grayDark"
                  >{{ day.day_name }}</span
                >
              </div>
              <div class="mt-2 grid grid-cols-7 gap-2">
                <div
                  v-for="day in weekActivity"
                  :key="day.date"
                  class="mx-auto"
                >
                  <template v-if="day.completed">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5"
                    >
                      <path
                        d="M10 20C14.1425 20 17.5 17.5 17.5 13.125C17.5 11.25 16.875 8.125 14.375 5.625C14.6875 7.5 12.8125 8.125 12.8125 8.125C13.75 5 11.25 0.625 7.5 0C7.94625 2.5 8.125 5 5 7.5C3.4375 8.75 2.5 10.9113 2.5 13.125C2.5 17.5 5.8575 20 10 20ZM10 18.75C7.92875 18.75 6.25 17.5 6.25 15.3125C6.25 14.375 6.5625 12.8125 7.8125 11.5625C7.65625 12.5 8.75 13.125 8.75 13.125C8.28125 11.5625 9.375 9.0625 11.25 8.75C11.0262 10 10.9375 11.25 12.5 12.5C13.2812 13.125 13.75 14.205 13.75 15.3125C13.75 17.5 12.0712 18.75 10 18.75Z"
                        fill="#222222"
                      />
                    </svg>
                  </template>
                  <template v-else>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5"
                    >
                      <g style="mix-blend-mode: difference">
                        <path
                          d="M10 20C14.1425 20 17.5 17.5 17.5 13.125C17.5 11.25 16.875 8.125 14.375 5.625C14.6875 7.5 12.8125 8.125 12.8125 8.125C13.75 5 11.25 0.625 7.5 0C7.94625 2.5 8.125 5 5 7.5C3.4375 8.75 2.5 10.9112 2.5 13.125C2.5 17.5 5.8575 20 10 20ZM10 18.75C7.92875 18.75 6.25 17.5 6.25 15.3125C6.25 14.375 6.5625 12.8125 7.8125 11.5625C7.65625 12.5 8.75 13.125 8.75 13.125C8.28125 11.5625 9.375 9.0625 11.25 8.75C11.0262 10 10.9375 11.25 12.5 12.5C13.2812 13.125 13.75 14.205 13.75 15.3125C13.75 17.5 12.0712 18.75 10 18.75Z"
                          fill="#222222"
                        />
                      </g>
                    </svg>
                  </template>
                </div>
              </div>
            </div>
          </div>

          <!-- Continue learning -->
          <div
            class="bg-primaryLight rounded-[12px] border border-grayLight p-6 text-bodyText"
          >
            <p class="text-[14px] font-semibold text-bodyText">
              Continue learning
            </p>

            <div
              class="mt-4 bg-primaryLight rounded-[10px] p-4 border border-grayLight"
            >
              <div class="flex items-start gap-4">
                <div class="flex-shrink-0">
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 40 43"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g filter="url(#filter0_d_14603_31516)">
                      <path
                        d="M15 35C12.2222 35 9.86111 34.0278 7.91667 32.0833C5.97222 30.1389 5 27.7778 5 25V15C5 12.2222 5.97222 9.86111 7.91667 7.91667C9.86111 5.97222 12.2222 5 15 5H25C27.7778 5 30.1389 5.97222 32.0833 7.91667C34.0278 9.86111 35 12.2222 35 15V25C35 27.7778 34.0278 30.1389 32.0833 32.0833C30.1389 34.0278 27.7778 35 25 35H15ZM18.3333 26.6667L28.3333 16.6667L26 14.3333L18.3333 22L14.6667 18.3333L12.3333 20.6667L18.3333 26.6667Z"
                        fill="#222222"
                      />
                    </g>
                    <defs>
                      <filter
                        id="filter0_d_14603_31516"
                        x="-4"
                        y="0"
                        width="48"
                        height="48"
                        filterUnits="userSpaceOnUse"
                        color-interpolation-filters="sRGB"
                      >
                        <feFlood
                          flood-opacity="0"
                          result="BackgroundImageFix"
                        />
                        <feColorMatrix
                          in="SourceAlpha"
                          type="matrix"
                          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                          result="hardAlpha"
                        />
                        <feOffset dy="4" />
                        <feGaussianBlur stdDeviation="2" />
                        <feComposite in2="hardAlpha" operator="out" />
                        <feColorMatrix
                          type="matrix"
                          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
                        />
                        <feBlend
                          mode="normal"
                          in2="BackgroundImageFix"
                          result="effect1_dropShadow_14603_31516"
                        />
                        <feBlend
                          mode="normal"
                          in="SourceGraphic"
                          in2="effect1_dropShadow_14603_31516"
                          result="shape"
                        />
                      </filter>
                    </defs>
                  </svg>
                </div>
                <div class="flex-1 space-y-3">
                  <p class="text-[13px] font-medium leading-5 text-bodyText">
                    {{ continueLearningTitle }}
                  </p>

                  <div class="space-y-2">
                    <div class="flex items-center gap-2">
                      <div
                        class="flex-1 h-2 bg-grayLight rounded-full overflow-hidden"
                      >
                        <div
                          class="h-2 bg-primaryDarkBg rounded-full"
                          :style="{ width: `${continueLearningProgress}%` }"
                        ></div>
                      </div>
                      <span class="text-[12px] text-grayDark"
                        >{{ continueLearningProgress }}% Complete</span
                      >
                    </div>
                    <div class="flex items-center gap-1.5">
                      <svg
                        width="17"
                        height="17"
                        viewBox="0 0 17 17"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M15.5834 8.49996C15.5834 12.41 12.4101 15.5833 8.50008 15.5833C4.59008 15.5833 1.41675 12.41 1.41675 8.49996C1.41675 4.58996 4.59008 1.41663 8.50008 1.41663C12.4101 1.41663 15.5834 4.58996 15.5834 8.49996Z"
                          stroke="#667085"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M11.128 10.7525L8.9322 9.44208C8.5497 9.21541 8.23804 8.67 8.23804 8.22375V5.31958"
                          stroke="#667085"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                      <span class="text-[12px] text-grayDark">{{
                        continueLearningMeta
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Daily Progress -->
        <div
          class="lg:col-span-2 bg-primaryLight rounded-[12px] border border-grayLight p-6"
        >
          <p class="text-[14px] font-semibold text-bodyText">Daily Progress</p>

          <div
            class="mt-4 bg-primaryLight rounded-[10px] border border-grayLight p-4"
          >
            <DailyProgressChart :data="dailyProgressData" />
          </div>
        </div>
      </div>

      <!-- Productivity heatmap -->
      <div class="bg-primaryLight rounded-[12px] border border-grayLight p-6">
        <div class="flex items-center justify-between mb-4">
          <div>
            <p class="text-[14px] font-semibold text-bodyText">
              Productivity
            </p>
          </div>
          <div class="flex items-center gap-2 text-[11px] text-grayDark">
            <div class="flex gap-1">
              <div class="w-3 h-3 rounded-[2px] bg-[#EBEDF0]"></div>
              <div class="w-3 h-3 rounded-[2px] bg-[#C6CBD1]"></div>
              <div class="w-3 h-3 rounded-[2px] bg-[#667085]"></div>
              <div class="w-3 h-3 rounded-[2px] bg-[#475467]"></div>
              <div class="w-3 h-3 rounded-[2px] bg-[#222222]"></div>
            </div>
          </div>
        </div>

        <div class="mt-4">
          <div class="w-full">
            <!-- Month labels -->
            <div class="flex mb-2">
              <div class="w-8 flex-shrink-0"></div>
              <div class="flex-1 flex justify-between pr-1">
                <span 
                  v-for="(m, idx) in months" 
                  :key="idx" 
                  class="text-[11px] text-grayDark"
                >{{ m }}</span>
              </div>
            </div>

            <div class="flex">
              <!-- Day labels -->
              <div class="w-8 flex-shrink-0 pr-2 text-[10px] text-grayDark flex flex-col justify-between">
                <div>Sun</div>
                <div></div>
                <div>Tue</div>
                <div></div>
                <div>Thu</div>
                <div></div>
                <div>Sat</div>
              </div>

              <!-- Heatmap grid -->
              <div class="flex-1 flex gap-[2px]">
                <div
                  v-for="(week, wIdx) in heatmap"
                  :key="wIdx"
                  class="flex flex-col gap-[2px] flex-1"
                >
                  <div
                    v-for="(lvl, dIdx) in week"
                    :key="dIdx"
                    class="aspect-square w-full rounded-[2px] cursor-pointer hover:ring-2 hover:ring-primaryDark transition-all"
                    :class="heatmapLevelClass(lvl)"
                    :title="getHeatmapTooltip(wIdx, dIdx)"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/axios";
import NavbarComponent from "@/components/NavbarComponent.vue";
import DailyProgressChart from "@/components/charts/DailyProgressChart.vue";
import eventBus from "@/eventBus";

export default {
  name: "TalentDashboardComponent",
  components: {
    NavbarComponent,
    DailyProgressChart,
  },
  data() {
    return {
      user: {},
      loading: true,

      goalLimit: null,

      talents: [],
      loadingTeam: true,

      learningGoals: [],
      loadingGoals: true,

      overview: {
        total_completed_goals: 0,
        total_active_tasks: 0,
        total_team_members: 0,
        completion_rate: 0,
      },
      loadingOverview: true,

      streak: {
        current_streak: 0,
        week_activity: [],
      },
      loadingStreak: true,

      recentLearningGoal: null,
      loadingRecentGoal: true,

      dailyProgressData: [],
      dailyProgressDays: 14,
      loadingDailyProgress: true,

      productivity: {
        productivity_data: [],
        total_tasks_completed: 0,
        days_tracked: 0,
      },
      loadingProductivity: true,

      months: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],
      dayLabels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    };
  },
  computed: {
    isManager() {
      return this.$store.getters.isManager;
    },
    isAdmin() {
      return this.$store.getters.isAdmin;
    },

    completedGoalsCount() {
      return this.overview.total_completed_goals || 0;
    },
    activeTasksCount() {
      return this.overview.total_active_tasks || 0;
    },
    teamMembersCount() {
      return this.overview.total_team_members || 0;
    },
    completionRate() {
      return this.overview.completion_rate || 0;
    },

    streakDays() {
      return this.streak.current_streak || 0;
    },
    weekActivity() {
      return this.streak.week_activity || [];
    },

    continueLearningTitle() {
      return this.recentLearningGoal?.title || "No active learning goal";
    },
    continueLearningProgress() {
      const p = this.recentLearningGoal?.progress;
      if (typeof p !== "number") return 0;
      return Math.max(0, Math.min(100, Math.round(p)));
    },
    continueLearningMeta() {
      const count = this.recentLearningGoal?.incomplete_subtasks;
      if (!count) return "";
      return `${count} lesson${count !== 1 ? 's' : ''} left`;
    },

    heatmap() {
      if (!this.productivity.productivity_data.length) {
        const weeks = 53;
        const days = 7;
        const out = [];
        for (let w = 0; w < weeks; w += 1) {
          const week = [];
          for (let d = 0; d < days; d += 1) {
            week.push(0);
          }
          out.push(week);
        }
        return out;
      }

      const productivityMap = {};
      this.productivity.productivity_data.forEach((item) => {
        productivityMap[item.date] = item.level;
      });

      const today = new Date();
      
      // Find the most recent Sunday (end of week)x
      const endDate = new Date(today);
      const dayOfWeek = endDate.getDay(); // 0 = Sunday, 6 = Saturday
      endDate.setDate(endDate.getDate() + (6 - dayOfWeek)); // Move to Saturday
      
      
      const startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - (52 * 7) + 1); // 52 weeks back, start on Sunday
      
      const startDayOfWeek = startDate.getDay();
      if (startDayOfWeek !== 0) {
        startDate.setDate(startDate.getDate() - startDayOfWeek);
      }

      const weeks = [];
      let currentDate = new Date(startDate);

      while (currentDate <= endDate) {
        const week = [];
        
        for (let day = 0; day < 7; day++) {
          const dateStr = currentDate.toISOString().split('T')[0];
          const level = productivityMap[dateStr] || 0;
          week.push(level);
          
          currentDate.setDate(currentDate.getDate() + 1);
        }
        
        weeks.push(week);
        
        if (weeks.length > 53) break;
      }

      return weeks;
    },

    totalTasksCompleted() {
      return this.productivity.total_tasks_completed || 0;
    },

    monthPositions() {
      const today = new Date();
      const endDate = new Date(today);
      const dayOfWeek = endDate.getDay();
      endDate.setDate(endDate.getDate() + (6 - dayOfWeek)); // Move to Saturday

      const startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - (52 * 7) + 1);
      const startDayOfWeek = startDate.getDay();
      if (startDayOfWeek !== 0) {
        startDate.setDate(startDate.getDate() - startDayOfWeek);
      }

      const cellWidth = 11;
      const gap = 3;
      const positions = [];
      const seenMonths = new Set();

      let currentDate = new Date(startDate);
      let weekIndex = 0;

      while (weekIndex < 53) {
        const month = currentDate.getMonth();
        const monthName = this.months[month];
        const monthKey = `${currentDate.getFullYear()}-${month}`;

        if (!seenMonths.has(monthKey) && currentDate.getDate() <= 7) {
          positions.push({
            name: monthName,
            offset: weekIndex * (cellWidth + gap),
          });
          seenMonths.add(monthKey);
        }

        currentDate.setDate(currentDate.getDate() + 7);
        weekIndex++;
      }

      return positions;
    },
  },
  methods: {
    emitOpenCreateProjectModal() {
      eventBus.emit("openCreateProjectModal");
    },
    heatmapLevelClass(level) {
      switch (level) {
        case 0:
          return "bg-[#EBEDF0]";
        case 1:
          return "bg-[#C6CBD1]";
        case 2:
          return "bg-[#667085]";
        case 3:
          return "bg-[#475467]";
        case 4:
          return "bg-[#222222]";
        default:
          return "bg-[#EBEDF0]";
      }
    },

    getHeatmapTooltip(weekIndex, dayIndex) {
      const today = new Date();
      
      const endDate = new Date(today);
      const dayOfWeek = endDate.getDay(); // 0 = Sunday, 6 = Saturday
      endDate.setDate(endDate.getDate() + (6 - dayOfWeek)); // Move to Saturday
      
      const startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - (52 * 7) + 1); // 52 weeks back
      
      const startDayOfWeek = startDate.getDay();
      if (startDayOfWeek !== 0) {
        startDate.setDate(startDate.getDate() - startDayOfWeek);
      }
      
      // Calculate the target date
      const targetDate = new Date(startDate);
      targetDate.setDate(targetDate.getDate() + (weekIndex * 7) + dayIndex);
      
      const dateStr = targetDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      
      const level = this.heatmap[weekIndex]?.[dayIndex] || 0;
      const activityText = level === 0 ? "No tasks" : `${level} task${level !== 1 ? 's' : ''}`;
      
      return `${activityText} on ${dateStr}`;
    },

    async fetchCurrentUser() {
      try {
        const response = await apiClient.post("/current_user");
        this.user = response.data.user || {};
        if (this.user.user_id) {
          await this.fetchGoalLimit(this.user.user_id);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        this.loading = false;
      }
    },

    async fetchGoalLimit(userId) {
      try {
        const response = await apiClient.get("/web/limit/", {
          params: {
            id: userId,
            name: "project",
            limit_type: "user",
          },
        });
        this.goalLimit = response.data.data;
      } catch (error) {
        console.error("Error fetching goal limit:", error);
      }
    },

    async fetchTalents() {
      try {
        this.loadingTeam = true;
        const response = await apiClient.get("/web/general/talents");
        this.talents = response.data.data || [];
      } catch (error) {
        console.error("Error fetching talents:", error);
      } finally {
        this.loadingTeam = false;
      }
    },

    async fetchLearningGoals() {
      try {
        this.loadingGoals = true;
        const response = await apiClient.get(
          "/web/learning-goals/my-learning-goals"
        );
        this.learningGoals = response.data || [];
      } catch (error) {
        console.error("Error fetching learning goals:", error);
      } finally {
        this.loadingGoals = false;
      }
    },

    async fetchOverview() {
      try {
        this.loadingOverview = true;
        const response = await apiClient.get("/web/dashboard/talent/overview");
        this.overview = response.data || {
          total_completed_goals: 0,
          total_active_tasks: 0,
          total_team_members: 0,
          completion_rate: 0,
        };
      } catch (error) {
        console.error("Error fetching learning goals overview:", error);
      } finally {
        this.loadingOverview = false;
      }
    },

    async fetchProductivity() {
      try {
        this.loadingProductivity = true;
        const response = await apiClient.get("/web/dashboard/talent/productivity", {
          params: {
            days: 365,
          },
        });
        this.productivity = response.data || {
          productivity_data: [],
          total_tasks_completed: 0,
          days_tracked: 0,
        };
      } catch (error) {
        console.error("Error fetching productivity data:", error);
      } finally {
        this.loadingProductivity = false;
      }
    },

    async fetchStreak() {
      try {
        this.loadingStreak = true;
        const response = await apiClient.get("/web/dashboard/talent/streak");
        this.streak = response.data || {
          current_streak: 0,
          week_activity: [],
        };
      } catch (error) {
        console.error("Error fetching learning streak:", error);
      } finally {
        this.loadingStreak = false;
      }
    },

    async fetchRecentLearningGoal() {
      try {
        this.loadingRecentGoal = true;
        const response = await apiClient.get("/web/dashboard/talent/recent");
        this.recentLearningGoal = response.data || null;
      } catch (error) {
        console.error("Error fetching recent learning goal:", error);
        this.recentLearningGoal = null;
      } finally {
        this.loadingRecentGoal = false;
      }
    },

    async fetchDailyProgress() {
      try {
        this.loadingDailyProgress = true;
        const response = await apiClient.get(
          "/web/dashboard/talent/daily-progress",
          {
            params: {
              days: this.dailyProgressDays,
            },
          }
        );
        
        const data = response.data.data_points.map((point) => {
          const date = new Date(point.date);
          const month = date.toLocaleString("en-US", { month: "short" });
          const day = date.getDate();
          
          return {
            label: `${month} ${day}`,
            value: point.productivity,
          };
        });
        
        this.dailyProgressData = data;
      } catch (error) {
        console.error("Error fetching daily progress chart:", error);
      } finally {
        this.loadingDailyProgress = false;
      }
    },
  },
  created() {
    this.fetchCurrentUser();
    this.fetchLearningGoals();
    this.fetchOverview();
    this.fetchProductivity();
    this.fetchStreak();
    this.fetchRecentLearningGoal();
    this.fetchDailyProgress();

    if (this.$store.getters.roleLoaded) {
      this.fetchTalents();
    } else {
      const unwatch = this.$store.watch(
        (state) => state.roleLoaded,
        (newValue) => {
          if (newValue) {
            this.fetchTalents();
            unwatch();
          }
        }
      );
    }
  },
};
</script>
